import React from "react";
import { Link } from "react-router-dom";
import { Check, Brain } from "lucide-react";

const PLANS = [
  { name: "Monthly", price: "$10", per: "/month", save: null, test: "pricing-monthly" },
  { name: "Annual", price: "$120", per: "/year", save: "2 months free", note: "First 60 days free — no card needed", test: "pricing-annual" },
  { name: "Founding Member", price: "$300", per: " once", save: "Lifetime", test: "pricing-lifetime",
    features: ["Everything in Annual", "Pay once — keep it forever", "Founding member badge", "Every future feature included"] },
];

const FEATURES = [
  "Full habit-loop journal",
  "Unlimited bills & expenses",
  "Provider pay-links",
  "Private & cancel anytime",
];

export default function PricingSection() {
  return (
    <section id="pricing" className="max-w-7xl mx-auto px-6 md:px-10 py-20">
      <div className="text-center max-w-2xl mx-auto">
        <p className="eyebrow text-[#607762]">Membership</p>
        <h2 className="font-serif text-4xl md:text-5xl mt-3">One membership. Three ways to pay.</h2>
        <p className="text-[#4d4f4c] mt-4">Pay monthly, yearly — or once, forever.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12 max-w-5xl mx-auto">
        {PLANS.map((p) => (
          <div
            key={p.name}
            className={`border p-8 bg-white ${p.test === "pricing-annual" ? "border-[#C05C3D]" : "border-[#E6E3DB]"}`}
            data-testid={p.test}
          >
            <div className="flex items-center justify-between">
              <p className="eyebrow">{p.name}</p>
              {p.save && <span className="text-xs bg-[#C05C3D] text-white px-2 py-1">{p.save}</span>}
            </div>
            <p className="font-serif text-6xl mt-4">
              {p.price}
              <span className="text-xl text-[#787671]">{p.per}</span>
            </p>
            {p.note && (
              <p className="mt-2 text-xs text-[#607762] tracking-wider uppercase">{p.note}</p>
            )}
            <ul className="mt-6 space-y-3 text-sm text-[#4d4f4c]">
              {(p.features || FEATURES).map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-[#607762] mt-0.5" /> {f}
                </li>
              ))}
            </ul>
            <Link
              to="/register"
              className="mt-8 inline-flex w-full items-center justify-center px-5 py-3 rounded-sm bg-[#2C302E] text-white hover:bg-[#1b1e1c] transition-colors"
              data-testid={`${p.test}-cta`}
            >
              {p.test === "pricing-lifetime" ? "Join once — forever" : `Start with ${p.name.toLowerCase()}`}
            </Link>
          </div>
        ))}
      </div>

      {/* "Not ready to commit?" Quiz CTA — softens the wall for ambivalent
          visitors and routes them into the funnel via the MoneyMind Quiz. */}
      <div
        className="mt-10 max-w-3xl mx-auto border border-[#E6E3DB] bg-[#FBFAF6] p-6 md:p-7 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6"
        data-testid="pricing-quiz-cta"
      >
        <div className="shrink-0 w-12 h-12 rounded-full bg-[#C05C3D]/10 flex items-center justify-center">
          <Brain className="h-6 w-6 text-[#C05C3D]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-serif text-xl text-[#2C302E]">Not ready to commit?</p>
          <p className="text-sm text-[#4d4f4c] mt-1">
            Take the 5-minute MoneyMind&trade; Quiz first. Discover your financial archetype,
            see your tailored 5-year plan — no card required.
          </p>
        </div>
        <Link
          to="/quiz?ref=pricing"
          className="shrink-0 inline-flex items-center gap-2 px-5 py-3 rounded-sm border-2 border-[#C05C3D] text-[#C05C3D] hover:bg-[#C05C3D] hover:text-white transition-colors text-sm font-medium"
          data-testid="pricing-quiz-cta-button"
        >
          <Brain className="h-4 w-4" /> Take the Quiz
        </Link>
      </div>
    </section>
  );
}
